import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Eye,
  Upload,
  Settings,
  Image as ImageIcon,
  BarChart3,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { usePhotographerEvents } from '@context/PhotographerEventsContext';
import { usePhotographer } from '@context/PhotographerContext';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import LoadingSpinner from '@components/common/LoadingSpinner';
import Alert from '@components/common/Alert';
import Modal from '@components/common/Modal';
import { WatermarkSettings } from '../components/gallery';
import ShowcaseControl from './ShowcaseControl';
import PublishControl from './PublishControl';
import { photographerAPI } from '@/services/photographerApi';
import type { PhotographerEvent } from '@/types/photographer';
import type { WatermarkConfig } from '@/types/watermark';
import { DEFAULT_WATERMARK_CONFIG } from '@/types/watermark';

type TabType = 'details' | 'gallery' | 'photos' | 'analytics';

const EventDetail: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { fetchEvent, updateEvent, deleteEvent, loading, error } = usePhotographerEvents();
  const { photographer } = usePhotographer();

  const [event, setEvent] = useState<PhotographerEvent | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [reapplySuccess, setReapplySuccess] = useState<string | null>(null);
  const [isReapplying, setIsReapplying] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState<Partial<PhotographerEvent>>({});

  // Load event on mount
  useEffect(() => {
    if (eventId) {
      loadEvent();
    }
  }, [eventId]);

  const loadEvent = async () => {
    if (!eventId) return;
    try {
      const eventData = await fetchEvent(eventId);
      setEvent(eventData);
      setFormData(eventData);
    } catch (err) {
      // Error handled by context
    }
  };

  const handleInputChange = useCallback((field: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setSaveSuccess(false);
  }, []);

  const handleNestedChange = useCallback((parent: string, field: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent as keyof PhotographerEvent] as Record<string, unknown> || {}),
        [field]: value,
      },
    }));
    setSaveSuccess(false);
  }, []);

  const handleWatermarkChange = useCallback((watermarkConfig: WatermarkConfig) => {
    setFormData(prev => {
      const currentSettings = prev.gallerySettings || {
        isPublic: true,
        requiresPassword: false,
        allowDownloads: true,
        showExifData: false,
        enableSocialSharing: true,
        watermarkEnabled: true,
      };
      return {
        ...prev,
        gallerySettings: {
          ...currentSettings,
          watermarkEnabled: watermarkConfig.enabled,
          watermarkConfig,
        },
      };
    });
    setSaveSuccess(false);
  }, []);

  const handleSave = async () => {
    if (!eventId || !formData) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const updatedEvent = await updateEvent(eventId, formData);
      setEvent(updatedEvent);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReapplyWatermark = async () => {
    if (!eventId || !formData) return;

    setIsReapplying(true);
    setSaveError(null);
    setReapplySuccess(null);

    try {
      const updatedEvent = await updateEvent(eventId, formData);
      setEvent(updatedEvent);
      setFormData(updatedEvent);
      const res = await photographerAPI.reapplyEventWatermark(eventId);
      setReapplySuccess(
        `Queued ${res.data.queued} photo${res.data.queued === 1 ? '' : 's'} for watermarking.`
      );
      setTimeout(() => setReapplySuccess(null), 5000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to reapply watermark');
    } finally {
      setIsReapplying(false);
    }
  };

  const handleDelete = async () => {
    if (!eventId) return;

    try {
      await deleteEvent(eventId);
      navigate('/dashboard/events');
    } catch (err) {
      // Error handled by context
    }
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'details', label: 'Details', icon: <Settings className="h-4 w-4" /> },
    { id: 'gallery', label: 'Gallery Settings', icon: <ImageIcon className="h-4 w-4" /> },
    { id: 'photos', label: 'Photos', icon: <Upload className="h-4 w-4" /> },
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="h-4 w-4" /> },
  ];

  if (loading && !event) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="p-6 space-y-4">
        <Alert
          type="error"
          title="Error loading event"
          message={error}
        />
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/dashboard/events')}>
            Go Back
          </Button>
          <Button variant="primary" onClick={loadEvent}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="p-6 space-y-4">
        <Alert
          type="warning"
          title="Event not found"
          message="The requested event could not be found."
        />
        <Button variant="primary" onClick={() => navigate('/dashboard/events')}>
          Go to Events
        </Button>
      </div>
    );
  }

  const watermarkConfig: WatermarkConfig = formData.gallerySettings?.watermarkConfig || {
    ...DEFAULT_WATERMARK_CONFIG,
    enabled: formData.gallerySettings?.watermarkEnabled ?? true,
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/events"
            className="inline-flex items-center px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{event.title}</h1>
            <p className="text-gray-600">
              {new Date(event.eventDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            icon={<Eye className="h-4 w-4" />}
            onClick={() => window.open(`/events/${event.id}`, '_blank')}
          >
            Preview
          </Button>
          <Button
            variant="primary"
            icon={<Save className="h-4 w-4" />}
            onClick={handleSave}
            loading={isSaving}
            disabled={isSaving}
          >
            Save Changes
          </Button>
        </div>
      </div>

      {/* Save Status */}
      {saveSuccess && (
        <Alert
          type="success"
          message="Changes saved successfully!"
          dismissible
          onDismiss={() => setSaveSuccess(false)}
        />
      )}
      {reapplySuccess && (
        <Alert
          type="success"
          message={reapplySuccess}
          dismissible
          onDismiss={() => setReapplySuccess(null)}
        />
      )}
      {saveError && (
        <Alert
          type="error"
          title="Save failed"
          message={saveError}
          dismissible
          onDismiss={() => setSaveError(null)}
        />
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow rounded-lg">
        {activeTab === 'details' && (
          <div className="p-6 space-y-6">
            <h2 className="text-lg font-medium text-gray-900">Event Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Event Title"
                value={formData.title || ''}
                onChange={e => handleInputChange('title', e.target.value)}
                required
              />

              <Input
                label="Event Date"
                type="date"
                value={formData.eventDate?.split('T')[0] || ''}
                onChange={e => handleInputChange('eventDate', e.target.value)}
                required
              />

              <Input
                label="Location"
                value={formData.location || ''}
                onChange={e => handleInputChange('location', e.target.value)}
                placeholder="City, State or Venue Name"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <PublishControl
                  eventId={event.id}
                  status={event.status}
                  onChange={(next) => setEvent({ ...event, status: next as 'draft' | 'published' | 'archived' })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={e => handleInputChange('description', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Describe your event..."
              />
            </div>

            {/* Pricing Section */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Input
                  label="Web Resolution"
                  type="number"
                  min={0}
                  step={0.01}
                  value={formData.pricing?.web || 0}
                  onChange={e => handleNestedChange('pricing', 'web', parseFloat(e.target.value))}
                  prefix="$"
                />
                <Input
                  label="Print Resolution"
                  type="number"
                  min={0}
                  step={0.01}
                  value={formData.pricing?.print || 0}
                  onChange={e => handleNestedChange('pricing', 'print', parseFloat(e.target.value))}
                  prefix="$"
                />
                <Input
                  label="Full Resolution"
                  type="number"
                  min={0}
                  step={0.01}
                  value={formData.pricing?.full || 0}
                  onChange={e => handleNestedChange('pricing', 'full', parseFloat(e.target.value))}
                  prefix="$"
                />
              </div>
            </div>

            {/* Album Showcase */}
            <ShowcaseControl eventId={event.id} />

            {/* Danger Zone */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-red-600 mb-4">Danger Zone</h3>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-red-800">Delete this event</h4>
                    <p className="text-sm text-red-600">
                      Once deleted, this event and all its photos cannot be recovered.
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={() => setShowDeleteModal(true)}
                  >
                    Delete Event
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'gallery' && (
          <div className="p-6 space-y-6">
            <h2 className="text-lg font-medium text-gray-900">Gallery Settings</h2>

            {/* Access Settings */}
            <div className="space-y-4">
              <h3 className="text-md font-medium text-gray-800">Access & Privacy</h3>

              <div className="flex items-center justify-between py-3 border-b">
                <div>
                  <p className="font-medium text-gray-900">Public Gallery</p>
                  <p className="text-sm text-gray-500">Allow anyone to view this gallery</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleNestedChange('gallerySettings', 'isPublic', !formData.gallerySettings?.isPublic)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    formData.gallerySettings?.isPublic ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                  role="switch"
                  aria-checked={formData.gallerySettings?.isPublic}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formData.gallerySettings?.isPublic ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between py-3 border-b">
                <div>
                  <p className="font-medium text-gray-900">Password Protection</p>
                  <p className="text-sm text-gray-500">Require a password to access this gallery</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleNestedChange('gallerySettings', 'requiresPassword', !formData.gallerySettings?.requiresPassword)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    formData.gallerySettings?.requiresPassword ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                  role="switch"
                  aria-checked={formData.gallerySettings?.requiresPassword}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formData.gallerySettings?.requiresPassword ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {formData.gallerySettings?.requiresPassword && (
                <Input
                  label="Gallery Password"
                  type="password"
                  value={formData.gallerySettings?.password || ''}
                  onChange={e => handleNestedChange('gallerySettings', 'password', e.target.value)}
                  placeholder="Enter gallery password"
                />
              )}

              <div className="flex items-center justify-between py-3 border-b">
                <div>
                  <p className="font-medium text-gray-900">Allow Downloads</p>
                  <p className="text-sm text-gray-500">Allow customers to download purchased photos</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleNestedChange('gallerySettings', 'allowDownloads', !formData.gallerySettings?.allowDownloads)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    formData.gallerySettings?.allowDownloads ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                  role="switch"
                  aria-checked={formData.gallerySettings?.allowDownloads}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formData.gallerySettings?.allowDownloads ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between py-3 border-b">
                <div>
                  <p className="font-medium text-gray-900">Social Sharing</p>
                  <p className="text-sm text-gray-500">Enable social media sharing buttons</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleNestedChange('gallerySettings', 'enableSocialSharing', !formData.gallerySettings?.enableSocialSharing)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    formData.gallerySettings?.enableSocialSharing ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                  role="switch"
                  aria-checked={formData.gallerySettings?.enableSocialSharing}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formData.gallerySettings?.enableSocialSharing ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Watermark Settings */}
            <div className="border-t pt-6">
              <h3 className="text-md font-medium text-gray-800 mb-4">Watermark Settings</h3>
              <WatermarkSettings
                config={watermarkConfig}
                onChange={handleWatermarkChange}
                photographerName={photographer?.businessName || photographer?.contactInfo?.name}
              />

              {/* Reprocess Images Button */}
              {event.totalPhotos > 0 && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-blue-800">Apply to Existing Photos</h4>
                      <p className="text-sm text-blue-600">
                        Reprocess all {event.totalPhotos} photos with the new watermark settings.
                        This may take several minutes.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      icon={<RefreshCw className="h-4 w-4" />}
                      onClick={handleReapplyWatermark}
                      loading={isReapplying}
                      disabled={isReapplying || isSaving}
                    >
                      {isReapplying ? 'Queueing...' : 'Reprocess All'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'photos' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-gray-900">Photos</h2>
              <Link
                to={`/dashboard/events/${eventId}/upload`}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Photos
              </Link>
            </div>

            <div className="text-center py-12">
              <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">
                {event.totalPhotos > 0 ? `${event.totalPhotos} photos` : 'No photos yet'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {event.totalPhotos > 0
                  ? 'Photo management features coming soon'
                  : 'Upload photos to your event gallery'}
              </p>
              {event.totalPhotos === 0 && (
                <div className="mt-6">
                  <Link
                    to={`/dashboard/events/${eventId}/upload`}
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Your First Photos
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Analytics</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500">Total Views</p>
                <p className="text-2xl font-bold text-gray-900">{event.analytics?.views || 0}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500">Unique Visitors</p>
                <p className="text-2xl font-bold text-gray-900">
                  {event.analytics?.uniqueVisitors || 0}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">
                  {event.analytics?.totalOrders || 0}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-500">Revenue</p>
                <p className="text-2xl font-bold text-green-600">
                  ${(event.analytics?.totalRevenue || 0).toFixed(2)}
                </p>
              </div>
            </div>

            <div className="mt-8 text-center py-12 border-t">
              <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                Detailed analytics and charts coming soon
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Event"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete "{event.title}"? This action cannot be undone and will
            also delete all associated photos and orders.
          </p>

          <div className="flex items-center justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={loading}>
              Delete Event
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EventDetail;
